define("Inputs/Dropdown", [`Input`, `module`], (Super, Self) => {
	return Super.inherit({
		namespace: Self.id,
		requires: [`Locale`, `XHR`],
		defaults: {
			hideValueOnOpen: true, // Whether or not the value should disappear when focusing on the input
			isIconAfterValue: false,
			listItemHasRadioButton: false, // Currently only used by FilterSearchBar.js
			selectOnTab: true, // Whether tabbing away selects the currently highlighted option
		},
		constructor(setup) {
			const { Locale, data, elements } = this;
			const { rules, validation } = data;
			const { listItemHasRadioButton, hideValueOnOpen, selectOnTab } = setup;
			// Prefer top-level setup; Form often nests flags under rules only
			const isIconAfterValue = setup.isIconAfterValue === true || rules.isIconAfterValue === true;
			const done = true;
			let listContainer;
			let resetButton;
			let arrowIcon;
			let promise;

			this.defaults(rules, {
				accessibilityId: null, // e.g. emailSignup, emailLogin, confirmEmailSignup, passwordSignup, passwordLogin - used for creating a unique static Id to associate the label to the textbox, should be UNIQUE on a given page
				filterByWord: false,
				filterWithTypeProperty: false,
				filterValueNormalizeFn: null,
				filterWordNormalizeFn: null,
			});

			this.defaults(validation, {
				required: true,
			});
			this.assign(data, {
				open: false,
				focused: false,
				query: ``,
				suggestedIndex: null,
				keysUsed: false,
				listItemHasRadioButton,
				hideValueOnOpen,
				isIconAfterValue,
				selectOnTab,
				filterByWord: false,
				filterWithTypeProperty: false,
				filterValueNormalizeFn: null,
				filterWordNormalizeFn: null,
			});

			this.render();
			this.addClass(`has-right-icon`); // for down-arrow / reset button spacing

			arrowIcon = this.get(`.icon-arrow-down`);
			listContainer = this.get(`ul`);
			resetButton = this.get(`[action="reset"]`);
			this.assign(elements, { arrowIcon, listContainer, resetButton });

			elements.options = []; // used for caching the corresponding li per option

			// Pin toggle chevron to the right edge (Input.styl height:100% flex can otherwise
			// stretch the icon-container and leave the glyph on the left).
			const toggleIconContainer = this.get(`[action="toggle"]`);
			if (this.isElement(toggleIconContainer)) {
				toggleIconContainer.style.setProperty(`position`, `absolute`, `important`);
				toggleIconContainer.style.setProperty(`top`, `0`, `important`);
				toggleIconContainer.style.setProperty(`bottom`, `0`, `important`);
				toggleIconContainer.style.setProperty(`left`, `auto`, `important`);
				toggleIconContainer.style.setProperty(`right`, `0`, `important`);
				toggleIconContainer.style.setProperty(`width`, `28px`, `important`);
				toggleIconContainer.style.setProperty(`height`, `auto`, `important`);
				toggleIconContainer.style.setProperty(`transform`, `none`, `important`);
				toggleIconContainer.style.setProperty(`display`, `flex`, `important`);
				toggleIconContainer.style.setProperty(`align-items`, `center`, `important`);
				toggleIconContainer.style.setProperty(`justify-content`, `center`, `important`);
				toggleIconContainer.style.setProperty(`z-index`, `50`, `important`);
				toggleIconContainer.style.setProperty(`padding`, `0`, `important`);
			}

			if (Number.isInteger(rules.optionsWidthPx)) {
				listContainer.style.width = `${rules.optionsWidthPx}px`;
			}

			this.hide({ done, target: listContainer });

			if (validation.required) {
				this.hide({ done, target: resetButton });
			}

			Locale.get({
				fireImmediately: true,
				subscribe: ({ selected }) => {
					data.useTranslatedLabel = selected.key !== `en_US`;
				},
			});

			promise = this.create({
				namespace: `Inputs/Textbox`,
				setup: {
					name: `query`,
					theme: setup.theme,
					rules: {
						autocomplete: false, // try to avoid the browser suggesting values
						gracePeriod: false, // don't wait to start triggering change callbacks
						label: rules.label,
						tooltip: rules.tooltip,
						errorMessage: rules.errorMessage,
						disabledMessage: rules.disabledMessage,
						prefix: rules.prefix,
						suffix: rules.suffix,
						accessibilityId: rules.accessibilityId,
					},
					validation: { type: `String` },
					callbacks: {
						focus: (evt) => {
							data.focused = true;
							this.openList();

							if (this.isFunction(this.callbacks.focus)) {
								this.callbacks.focus();
							}
						},
						blur: (evt) => {
							if (evt.relatedTarget === listContainer) {
								// Chrome focuses a scrollable container when its scrollbar is grabbed, which
								// is not the field being left. The list's own blur takes over from here.
								return;
							}

							this.closeAfterBlur();
						},
						change: (value) => {
							const options = this.isArray(rules.options) ? rules.options : [];
							const { filterByWord, filterWithTypeProperty, filterValueNormalizeFn, filterWordNormalizeFn } = rules;
							let matched = true;
							let count = 0;
							let index;

							if (this.isString(value, 1)) {
								value = value.toUpperCase();

								if (this.isFunction(filterValueNormalizeFn)) {
									value = filterValueNormalizeFn(value);
								}
							}

							this.chain(options)
								.clone()
								.sortBy((option) => {
									const { queryString } = option;
									let result = count;

									if (this.isString(value, 1)) {
										let match;
										const label = option.label.toUpperCase();
										const matchType = filterWithTypeProperty && this.isString(option.type, 1);
										const type = matchType ? option.type.toUpperCase() : null;

										if (filterByWord) {
											let words = label.split(` `);

											if (matchType) {
												words.push(...type.split(` `));
											}

											if (this.isFunction(filterWordNormalizeFn)) {
												words = words.map(filterWordNormalizeFn);
											}

											match = false;

											for (let i = 0; i < words.length; i++) {
												const valueWords = value.trim().split(` `);

												for (let j = 0; j < valueWords.length; j++) {
													if (words.some((word) => word.indexOf(valueWords[j]) === 0 && j >= i)) {
														match = true;
													} else {
														match = false;
														break;
													}
												}

												if (match === true) {
													break;
												}
											}
										} else if (this.isString(queryString, 1)) {
											const searchTerms = value.split(` `);
											match = this.find(searchTerms, (term) => queryString.indexOf(term) >= 0);
										}

										if (this.isString(match, 1) || match === true) {
											option.match = true;
											result = count - 1000000; // boost to the top
										} else {
											option.match = false;
										}
									} else {
										// no filter present
										option.match = true;
									}

									count++;

									return result;
								})
								.each((option, i) => {
									const element = this.elements.options[option.index];
									listContainer.appendChild(element);

									if (option.match) {
										this.removeClass(`divider`, element);
									} else {
										if (matched) {
											this.addClass(`divider`, element);
										} else {
											this.removeClass(`divider`, element);
										}

										matched = false;
									}

									if (i === 0) {
										index = option.index;
									}
								});

							data.keysUsed = true;
							this.suggestIndex(index);
						},
						submit: (value) => {
							if (this.isInt(data.suggestedIndex)) {
								this.selectIndex(data.suggestedIndex);
							}
							this.closeList();
						},
					},
				},
			});

			return this.when({
				promises: [rules.options, promise],
				spread: (options) => {
					if (this.isArray(options)) {
						this.populate(options);
					}
				},
			});
		},

		// MARK: API
		api: {
			populate: {
				value(options) {
					const { data, elements, instances } = this;
					const { listItemHasRadioButton } = data;
					const { listContainer } = elements;
					const elementList = [];
					let currentValue;
					let listData = {};

					currentValue = this.getValue(); // cache, to try re-setting after populating options

					if (this.isUndefined(currentValue)) {
						currentValue = null;
					}

					options = this.clone(options); // avoid the possibility of being manipulated externally
					this.assign(listData, {
						options,
						listItemHasRadioButton,
					});

					if (!data.validation.required) {
						options.unshift({
							label: ``,
							value: null,
						});
					}

					this.render({
						name: `list`,
						target: listContainer,
						data: listData,
					});

					this.each(options, (option, i) => {
						const element = this.get(`[index="${i}"]`, listContainer);
						const { alias, label, value } = option;
						let queryString = label;

						elementList.push(element);
						option.index = i; // cache for easier re-sorting later

						if (this.isString(value)) {
							queryString += ` ${value}`;
						}

						if (this.isString(alias)) {
							queryString += ` ${alias}`;
						}

						option.queryString = queryString.toUpperCase();
					});

					data.rules.options = options;
					elements.options = elementList;
					this.setValue(currentValue, true);
					instances.query.setValue(null, true);
				},
			},

			suggestIndex: {
				value(suggestedIndex) {
					const { data, elements } = this;
					const { listContainer } = elements;

					this.each(data.rules.options, ({ index }) => {
						const target = elements.options[index];

						if (suggestedIndex === index) {
							data.suggestedIndex = suggestedIndex;
							this.addClass(`suggested`, target);

							const containerTop = listContainer.scrollTop;
							const containerBottom = containerTop + listContainer.clientHeight;
							const targetTop = target.offsetTop;
							const targetBottom = targetTop + target.offsetHeight;

							if (targetTop < containerTop) {
								listContainer.scrollTop = targetTop;
							} else if (targetBottom > containerBottom) {
								listContainer.scrollTop = targetBottom - listContainer.clientHeight;
							}
						} else {
							this.removeClass(`suggested`, target);
						}
					});
				},
			},

			selectIndex: {
				value(index) {
					const { options } = this.data.rules;
					const option = options[index];
					const value = this.getValue();

					if (value !== option.value && !option.disabled) {
						this.setValue(option.value);
					}
				},
			},

			syncLabel: {
				value() {
					const { data, instances } = this;
					const { query: input } = instances;
					const { hideValueOnOpen, open, rules, useTranslatedLabel } = data;
					const value = this.getValue();

					if ((hideValueOnOpen && open) || this.isNull(value)) {
						const { label } = rules;
						input.updateRules({ label });
						input.setValue(null, true);
					} else if (!this.data.open || !hideValueOnOpen) {
						const { options, useValueAsLabel } = rules;
						this.find(options, (option) => {
							if (this.isEqual(option.value, value)) {
								if (useTranslatedLabel) {
									let selected = this.get(`ul > li.selected`);

									if (this.isElement(selected)) {
										input.setValue(selected.innerText, true);
									} else {
										input.setValue(option.label, true);
									}
								} else if (useValueAsLabel) {
									input.setValue(option.value, true);
								} else {
									input.setValue(option.label, true);
								}
								return true; // break loop
							}
						});
					}
				},
			},

			closeAfterBlur: {
				value() {
					const { data } = this;

					data.focused = false;

					if (data.selectOnTab && data.keysUsed && this.isInt(data.suggestedIndex)) {
						this.selectIndex(data.suggestedIndex);
					}

					this.closeList();

					if (this.isFunction(this.callbacks.blur)) {
						this.callbacks.blur();
					}
				},
			},

			setValue: {
				public: true,
				value(context, returned, value, skipCallbacks) {
					const { data, elements } = this;
					const { rules, validation } = data;
					const { arrowIcon: target } = elements;
					const done = true;
					let found = false;

					if (this.isNull(returned)) {
						// not a valid value
						this.show({ done, target });
					} else {
						// a value is present
						if (validation.required) {
							// not allowed to cancel
							this.show({ done, target });
						} else {
							this.hide({ target, done });
						}
					}

					data.suggestedIndex = null;
					this.each(rules.options, (option) => {
						const element = elements.options[option.index];

						if (this.isEqual(option.value, returned)) {
							this.addClass(`selected`, element);
							data.suggestedIndex = option.index;
							found = true;
						} else {
							this.removeClass(`selected`, element);
						}
					});

					if (!found && !this.isNull(returned)) {
						this.setValue(null, true);
					}

					this.syncLabel();
				},
			},

			updateRules: {
				public: true,
				value(context, returned, updates) {
					const { instances, syncLabel } = this;
					const { query } = instances;
					const { icon, suffix, prefix } = updates;
					let promise;

					if (this.isString(icon)) {
						query.updateRules({ icon });
					}

					if (this.isString(suffix)) {
						query.updateRules({ suffix });
					}

					if (this.isString(prefix)) {
						query.updateRules({ prefix });
					}

					if (this.isDefined(updates.options)) {
						promise = this.populate(updates.options);
					}

					// in case a label update was passed
					this.when({ promise, then: syncLabel, done: true });
				},
			},

			updateValidation: {
				public: true,
				value(context, returned, updates) {
					const { validation } = this.data;
					const options = { done: true, selector: `[action="reset"]` };

					if (validation.required) {
						this.hide(options);
					} else {
						this.show(options);
					}
				},
			},

			disable: {
				public: true,
				value(context, returned) {
					this.instances.query.disable();
				},
			},

			enable: {
				public: true,
				value(context, returned) {
					this.instances.query.enable();
				},
			},

			focus: {
				public: true,
				value(context, returned) {
					this.instances.query.focus();
				},
			},

			blur: {
				public: true,
				value(context, returned) {
					this.instances.query.blur();
				},
			},

			openList: {
				public: true,
				value(context, returned) {
					const { data, elements, instances } = this;
					const { listContainer } = elements;
					const { query } = instances;
					const done = true;
					const value = this.getValue();
					const option = this.find(data.rules.options, (opt) => this.isEqual(value, opt.value));
					const index = this.isObject(option) ? option.index : 0;

					if (data.open) {
						return;
					}

					data.open = true;
					data.keysUsed = false;
					this.addClass(`focus`);

					query.setValue(null);
					this.syncLabel();

					// TODO: suggestIndex() can't be called here (too early), but not setting the
					// variable leaves unset in the interim (arrow key problems, etc.)
					this.show({
						target: listContainer,
						transition: `fade`,
						then: () => {
							const [target, fully] = [listContainer, true];
							const inViewport = this.DOM.inViewport({ fully, target });

							if (!inViewport) {
								this.addClass(`inverted`);
							}

							return this.defer({
								duration: 20,
								then: () => this.suggestIndex(index),
							});
						},
						done,
					});
				},
			},

			closeList: {
				public: true,
				value(context, returned) {
					if (!this.data.open) {
						return;
					}

					const { listContainer } = this.elements;
					const done = true;

					this.data.open = false;
					this.removeClass(`focus`);
					this.hide({
						target: listContainer,
						transition: `fade`,
						then: () => this.removeClass(`inverted`),
						done,
					});
					this.syncLabel();
				},
			},

			onValidateError: {
				public: true,
				value(context, returned, reason) {
					this.instances.query.onValidateError(reason);
				},
			},

			onValidateSuccess: {
				public: true,
				value(context, returned) {
					this.instances.query.onValidateSuccess();
				},
			},
		},

		// MARK: Listeners
		listeners: [
			{
				event: `mousedown`,
				filter: `[action="reset"]`,
				handler(evt) {
					if (this.data.enabled) {
						evt.preventDefault(); // prevent blur on the query input
						this.setValue(null);
						this.instances.query.focus();
					}
				},
			},
			{
				event: `keydown`,
				filter: `[action="reset"]`,
				handler(evt) {
					if (this.data.enabled) {
						switch (evt.key) {
							case `Enter`:
							case ` `: {
								evt.preventDefault();
								this.setValue(null);
								this.instances.query.focus();
								break;
							}
						}
					}
				},
			},
			{
				event: `mousedown`,
				filter: `[action="toggle"]`,
				handler(evt) {
					if (this.data.enabled) {
						evt.preventDefault(); // prevent blur on the query input

						if (this.data.open) {
							this.closeList();
						} else {
							this.openList();
							this.instances.query.focus();
						}
					}
				},
			},
			{
				event: `mousedown`,
				filter: `li[index]`,
				handler(evt) {
					const link = evt.currentTarget;
					const index = parseInt(link.getAttribute(`index`), 10);

					evt.stopPropagation(); // prevent bubbling to [action="toggle"] which would re-open the list
					this.selectIndex(index);
					this.closeList();
				},
			},
			{
				event: `blur`,
				filter: `ul`,
				handler(evt) {
					const { relatedTarget } = evt;

					// focus returning to the query (or anywhere else in the field) isn't leaving it
					if (this.isElement(relatedTarget) && this.container.contains(relatedTarget)) {
						return;
					}

					this.closeAfterBlur();
				},
			},
			{
				event: `mouseup`,
				target: window,
				matchContainer: false,
				handler() {
					const { data, elements, instances } = this;

					// a scrollbar drag leaves focus on the list, so hand it back to the query
					if (data.open && document.activeElement === elements.listContainer) {
						instances.query.focus();
					}
				},
			},
			{
				event: `keydown`,
				target: `input`,
				matchContainer: false,
				handler(evt) {
					const { data, elements } = this;
					let element;
					let sibling;
					let index;

					if (!data.open && data.focused && evt.key.length === 1 && !evt.metaKey && !evt.ctrlKey) {
						this.openList();
					}

					switch (evt.keyCode) {
						case 27: // ESC
							evt.preventDefault();
							evt.stopPropagation(); // avoid bubbling to close overlays, etc.
							data.suggestedIndex = null; // ignore the suggested value, keep the selected one
							this.closeList();
							break;

						case 38: // Up arrow
						case 40: // Down arrow
							if (!data.open && data.focused) {
								this.openList();
								break;
							}
							element = elements.options[data.suggestedIndex];
							if (this.isElement(element)) {
								// There is a currently selected element
								sibling = evt.keyCode === 38 ? element.previousElementSibling : element.nextElementSibling;
								if (this.isElement(sibling)) {
									index = parseInt(sibling.getAttribute(`index`), 10);
									data.keysUsed = true;
									this.suggestIndex(index);
								}
							}
							break;
					}
				},
			},
		],
	});
});

define("Inputs/Dropdown.templates",["handlebars"], function(Handlebars) {

this["Templates"] = this["Templates"] || {};

this["Templates"]["Inputs/Dropdown/list"] = Handlebars.template({"1":function(container,depth0,helpers,partials,data) {
    var stack1, alias1=container.lambda, alias2=depth0 != null ? depth0 : (container.nullContext || {}), lookupProperty = container.lookupProperty || function(parent, propertyName) {
        if (Object.prototype.hasOwnProperty.call(parent, propertyName)) {
          return parent[propertyName];
        }
        return undefined
    };

  return " <li index=\""
    + container.escapeExpression(alias1((data && lookupProperty(data,"index")), depth0))
    + "\""
    + ((stack1 = lookupProperty(helpers,"if").call(alias2,(depth0 != null ? lookupProperty(depth0,"divider") : depth0),{"name":"if","hash":{},"fn":container.program(2, data, 0),"inverse":container.noop,"data":data,"loc":{"start":{"line":1,"column":40},"end":{"line":1,"column":78}}})) != null ? stack1 : "")
    + ((stack1 = lookupProperty(helpers,"if").call(alias2,(depth0 != null ? lookupProperty(depth0,"disabled") : depth0),{"name":"if","hash":{},"fn":container.program(4, data, 0),"inverse":container.noop,"data":data,"loc":{"start":{"line":1,"column":78},"end":{"line":1,"column":118}}})) != null ? stack1 : "")
    + ((stack1 = lookupProperty(helpers,"if").call(alias2,(depth0 != null ? lookupProperty(depth0,"notranslate") : depth0),{"name":"if","hash":{},"fn":container.program(6, data, 0),"inverse":container.noop,"data":data,"loc":{"start":{"line":1,"column":118},"end":{"line":1,"column":158}}})) != null ? stack1 : "")
    + "> <p class=\"label\">"
    + ((stack1 = alias1((depth0 != null ? lookupProperty(depth0,"label") : depth0), depth0)) != null ? stack1 : "")
    + "</p> "
    + ((stack1 = lookupProperty(helpers,"if").call(alias2,(depth0 != null ? lookupProperty(depth0,"tooltip") : depth0),{"name":"if","hash":{},"fn":container.program(8, data, 0),"inverse":container.noop,"data":data,"loc":{"start":{"line":1,"column":193},"end":{"line":1,"column":299}}})) != null ? stack1 : "")
    + " "
    + ((stack1 = lookupProperty(helpers,"if").call(alias2,((stack1 = (data && lookupProperty(data,"root"))) && lookupProperty(stack1,"listItemHasRadioButton")),{"name":"if","hash":{},"fn":container.program(10, data, 0),"inverse":container.noop,"data":data,"loc":{"start":{"line":1,"column":300},"end":{"line":1,"column":410}}})) != null ? stack1 : "")
    + " "
    + ((stack1 = lookupProperty(helpers,"if").call(alias2,(depth0 != null ? lookupProperty(depth0,"rightIcon") : depth0),{"name":"if","hash":{},"fn":container.program(12, data, 0),"inverse":container.noop,"data":data,"loc":{"start":{"line":1,"column":411},"end":{"line":1,"column":490}}})) != null ? stack1 : "")
    + " </li> ";
},"2":function(container,depth0,helpers,partials,data) {
    return " class=\"divider\"";
},"4":function(container,depth0,helpers,partials,data) {
    return " class=\"disabled\"";
},"6":function(container,depth0,helpers,partials,data) {
    return "translate=\"no\"";
},"8":function(container,depth0,helpers,partials,data) {
    var alias1=container.escapeExpression, lookupProperty = container.lookupProperty || function(parent, propertyName) {
        if (Object.prototype.hasOwnProperty.call(parent, propertyName)) {
          return parent[propertyName];
        }
        return undefined
    };

  return " <span class=\"info\" data-tooltip-text=\""
    + alias1(container.lambda((depth0 != null ? lookupProperty(depth0,"tooltip") : depth0), depth0))
    + "\"> "
    + alias1(lookupProperty(helpers,"getIcon").call(depth0 != null ? depth0 : (container.nullContext || {}),"moreinfo",{"name":"getIcon","hash":{},"data":data,"loc":{"start":{"line":1,"column":261},"end":{"line":1,"column":283}}}))
    + " </span> ";
},"10":function(container,depth0,helpers,partials,data) {
    return " <div class=\"radio-button\"> <div class=\"radio-inner\"></div> </div> ";
},"12":function(container,depth0,helpers,partials,data) {
    var lookupProperty = container.lookupProperty || function(parent, propertyName) {
        if (Object.prototype.hasOwnProperty.call(parent, propertyName)) {
          return parent[propertyName];
        }
        return undefined
    };

  return " <span class=\"right-icon\">"
    + container.escapeExpression(lookupProperty(helpers,"getIcon").call(depth0 != null ? depth0 : (container.nullContext || {}),(depth0 != null ? lookupProperty(depth0,"rightIcon") : depth0),{"name":"getIcon","hash":{},"data":data,"loc":{"start":{"line":1,"column":454},"end":{"line":1,"column":475}}}))
    + "</span> ";
},"compiler":[8,">= 4.3.0"],"main":function(container,depth0,helpers,partials,data) {
    var stack1, lookupProperty = container.lookupProperty || function(parent, propertyName) {
        if (Object.prototype.hasOwnProperty.call(parent, propertyName)) {
          return parent[propertyName];
        }
        return undefined
    };

  return ((stack1 = lookupProperty(helpers,"each").call(depth0 != null ? depth0 : (container.nullContext || {}),(depth0 != null ? lookupProperty(depth0,"options") : depth0),{"name":"each","hash":{},"fn":container.program(1, data, 0),"inverse":container.noop,"data":data,"loc":{"start":{"line":1,"column":0},"end":{"line":1,"column":506}}})) != null ? stack1 : "");
},"useData":true});

this["Templates"]["Inputs/Dropdown/main"] = Handlebars.template({"1":function(container,depth0,helpers,partials,data) {
    var lookupProperty = container.lookupProperty || function(parent, propertyName) {
        if (Object.prototype.hasOwnProperty.call(parent, propertyName)) {
          return parent[propertyName];
        }
        return undefined
    };

  return " <div class=\"icon-container right\" action=\"toggle\" tabindex=\"-1\">  "
    + container.escapeExpression(lookupProperty(helpers,"getIcon").call(depth0 != null ? depth0 : (container.nullContext || {}),"arrow-down",{"name":"getIcon","hash":{},"data":data,"loc":{"start":{"line":1,"column":126},"end":{"line":1,"column":150}}}))
    + " </div> ";
},"compiler":[8,">= 4.3.0"],"main":function(container,depth0,helpers,partials,data) {
    var stack1, alias1=depth0 != null ? depth0 : (container.nullContext || {}), alias2=container.escapeExpression, lookupProperty = container.lookupProperty || function(parent, propertyName) {
        if (Object.prototype.hasOwnProperty.call(parent, propertyName)) {
          return parent[propertyName];
        }
        return undefined
    };

  return ((stack1 = lookupProperty(helpers,"unless").call(alias1,(depth0 != null ? lookupProperty(depth0,"isIconAfterValue") : depth0),{"name":"unless","hash":{},"fn":container.program(1, data, 0),"inverse":container.noop,"data":data,"loc":{"start":{"line":1,"column":0},"end":{"line":1,"column":169}}})) != null ? stack1 : "")
    + " <div name=\"query\"></div> "
    + ((stack1 = lookupProperty(helpers,"if").call(alias1,(depth0 != null ? lookupProperty(depth0,"isIconAfterValue") : depth0),{"name":"if","hash":{},"fn":container.program(1, data, 0),"inverse":container.noop,"data":data,"loc":{"start":{"line":1,"column":195},"end":{"line":1,"column":356}}})) != null ? stack1 : "")
    + " <span class=\"icon-container right\" action=\"reset\" role=\"button\" tabindex=\"0\" aria-label=\"Reset "
    + alias2(container.lambda(((stack1 = (depth0 != null ? lookupProperty(depth0,"rules") : depth0)) != null ? lookupProperty(stack1,"label") : stack1), depth0))
    + "\">  "
    + alias2(lookupProperty(helpers,"getIcon").call(alias1,"close",{"name":"getIcon","hash":{},"data":data,"loc":{"start":{"line":1,"column":500},"end":{"line":1,"column":519}}}))
    + " </span> <ul tabindex=\"-1\"></ul> ";
},"useData":true});

return this["Templates"];

});
//# sourceMappingURL=Dropdown.js.map